// ── Delivery Approach Section Editor ───────────────────────────────────────────
// Vertical flow chart: numbered circles + title + description + connecting lines

import { Plus, Trash2, GripVertical } from 'lucide-react';
import { uid } from '../../lib/uid';


export default function DeliveryApproachSection({ data, onChange }) {
  const approach = data || { title: 'DELIVERY APPROACH', steps: [] };

  const update = (key, value) => {
    onChange({ ...approach, [key]: value });
  };

  const addStep = () => {
    const steps = [...(approach.steps || [])];
    const nextNum = steps.length + 1;
    steps.push({ id: uid(), step_number: nextNum, title: '', description: '' });
    update('steps', steps);
  };

  const removeStep = (idx) => {
    const steps = [...(approach.steps || [])];
    steps.splice(idx, 1);
    // Re-number
    steps.forEach((s, i) => { s.step_number = i + 1; });
    update('steps', steps);
  };

  const updateStep = (idx, field, value) => {
    const steps = [...(approach.steps || [])];
    steps[idx] = { ...steps[idx], [field]: value };
    update('steps', steps);
  };

  const moveStep = (idx, direction) => {
    const newIdx = idx + direction;
    const steps = [...(approach.steps || [])];
    if (newIdx < 0 || newIdx >= steps.length) return;
    [steps[idx], steps[newIdx]] = [steps[newIdx], steps[idx]];
    // Re-number
    steps.forEach((s, i) => { s.step_number = i + 1; });
    update('steps', steps);
  };

  return (
    <div className="space-y-4">
      {/* Section title */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
          Section Title
        </label>
        <input
          type="text"
          value={approach.title || ''}
          onChange={(e) => update('title', e.target.value)}
          placeholder="DELIVERY APPROACH"
          className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white font-semibold tracking-wider"
        />
      </div>

      {/* Steps */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-2">
          Steps
        </label>
        {(approach.steps || []).map((step, idx) => (
          <div key={step.id} className="relative mb-0">
            <div className="flex items-stretch gap-3">
              {/* Step number + connecting line */}
              <div className="flex flex-col items-center flex-shrink-0">
                <span className="w-8 h-8 rounded-full bg-[#c9a84c] text-[#1a1a2e] text-sm font-bold flex items-center justify-center">
                  {step.step_number}
                </span>
                {/* Connecting line to next step */}
                {idx < (approach.steps || []).length - 1 && (
                  <div className="w-0.5 flex-1 bg-[#c9a84c]/30 my-1" style={{ minHeight: '20px' }} />
                )}
              </div>

              {/* Step content */}
              <div className="flex-1 p-3 bg-[#f8f7f4] rounded-lg border border-[#e5e4df] mb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#6b7280]">Step {step.step_number}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveStep(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] disabled:opacity-30 transition-colors"
                      title="Move up"
                    >
                      <GripVertical className="w-3.5 h-3.5 rotate-180" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(idx, 1)}
                      disabled={idx === (approach.steps || []).length - 1}
                      className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] disabled:opacity-30 transition-colors"
                      title="Move down"
                    >
                      <GripVertical className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeStep(idx)}
                      className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                      title="Remove step"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mb-2">
                  <input
                    type="text"
                    value={step.title || ''}
                    onChange={(e) => updateStep(idx, 'title', e.target.value)}
                    placeholder="Step title"
                    className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white font-semibold"
                  />
                </div>
                <textarea
                  value={step.description || ''}
                  onChange={(e) => updateStep(idx, 'description', e.target.value)}
                  placeholder="Describe this step..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addStep}
          className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#b8973e] font-medium mt-2"
        >
          <Plus className="w-3.5 h-3.5" />
          Add step
        </button>
      </div>
    </div>
  );
}
